import React from 'react'
import styles from './RadialProgress.sass'
import { useSpring, animated } from 'react-spring'

interface RadialProgressProps {
  size?: number
  progress?: number
  thickness?: number
  stroke?: string
  ringStroke?: string
  roundedLine?: boolean
  children?: React.ReactNode
}

const RadialProgress = ({
  size = 80,
  progress = 0,
  thickness = 5,
  stroke = 'var(--theme-darken)',
  ringStroke = '#eaeaea',
  roundedLine,
  children,
}: RadialProgressProps) => {
  size = size - thickness * 2
  const initialAnimatedProps = { progress, opacity: progress > 0 ? 1 : 0 }
  const animatedProps =
    useSpring<typeof initialAnimatedProps>(initialAnimatedProps)
  return (
    <div className={styles.root}>
      <svg
        width={size + thickness * 2}
        height={size + thickness * 2}
        className={styles.svg}
      >
        <circle
          className={styles.circle}
          cx={size / 2 + thickness}
          cy={size / 2 + thickness}
          r={size / 2}
          stroke={ringStroke}
          strokeWidth={thickness}
        />
        <animated.path
          className={styles.path}
          strokeWidth={thickness}
          stroke={stroke}
          opacity={animatedProps.opacity}
          strokeLinecap={roundedLine ? 'round' : undefined}
          d={computePath(size, thickness)}
          strokeDasharray={animatedProps.progress.to((v) =>
            convertProgressToDasharray(v, size),
          )}
        />
      </svg>
      {children}
    </div>
  )
}

export default RadialProgress

/**
 * Generates a path based on size and stroke width.
 */
function computePath(size: number, strokeWidth: number) {
  const half = size / 2
  return `
    M${half + strokeWidth} ${strokeWidth}
    a ${half} ${half} 0 0 1 0 ${size}
    a ${half} ${half} 0 0 1 0 -${size}
  `
}

/**
 * Calculates the correct dasharray to emulate the progress
 * effect.
 *
 * Explanation (for myself when I forget):
 * The trick is to set the length of each dash to
 * whatever % of the circumference we're at, and at the
 * same time make sure the gap between each stroke is far
 * enough so only a single one is displayed at any time.
 */
function convertProgressToDasharray(progress: number, size: number) {
  const circumference = size * Math.PI // mmmm, could go for some pie
  // Length of the stroke
  const length = (progress / 100) * circumference
  // The gap which will make sure only a single "dash" is drawn.
  const gap = circumference - length
  return `${length},${gap}`
}
